import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, CreditCard, Loader2, Wallet, Zap, CheckCircle } from 'lucide-react';
import { paymentsApi } from '../lib/api';
import { toast } from 'sonner';

const BALANCE_PACKAGES = [
  { id: 'small', label: '5.00', amount: 5 },
  { id: 'medium', label: '10.00', amount: 10 },
  { id: 'large', label: '25.00', amount: 25 },
  { id: 'xl', label: '50.00', amount: 50 },
];

const StripeCheckoutModal = ({ isOpen, onClose, paymentType, creatorId, productId, filmId, donationAmount, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('medium');
  const [customDonation, setCustomDonation] = useState(donationAmount || 2.00);
  const [savedCard, setSavedCard] = useState(null);
  const [userBalance, setUserBalance] = useState(0);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!isOpen) { setResult(null); return; }
    const fetchInfo = async () => {
      setLoadingInfo(true);
      try {
        const [cardData, balanceData] = await Promise.all([
          paymentsApi.getSavedCard().catch(() => ({ card: null })),
          paymentsApi.getBalance().catch(() => ({ balance: 0 })),
        ]);
        setSavedCard(cardData.card);
        setUserBalance(balanceData.balance || 0);
      } catch { /* ignore */ }
      setLoadingInfo(false);
    };
    fetchInfo();
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePayWithSavedCard = async () => {
    setLoading(true);
    try {
      let res;
      if (paymentType === 'add_balance') {
        res = await paymentsApi.topupBalance(selectedPackage, window.location.origin);
      } else if (paymentType === 'subscription') {
        res = await paymentsApi.subscribe('premium', window.location.origin);
      } else {
        const payload = {
          payment_type: paymentType,
          origin_url: window.location.origin,
          creator_id: creatorId || undefined,
          product_id: productId || undefined,
          film_id: filmId || undefined,
          amount: paymentType === 'donation' ? parseFloat(customDonation) : undefined,
          metadata: {},
        };
        res = await paymentsApi.createCheckout(payload);
      }

      if (res.status === 'completed' || res.status === 'active') {
        setResult(res);
        toast.success(paymentType === 'subscription' ? 'Subscription activated!' : 'Payment successful!');
        if (onSuccess) onSuccess(res);
      } else if (res.checkout_url || res.status === 'checkout_required') {
        window.location.href = res.checkout_url;
      }
    } catch (error) {
      toast.error(error.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckoutRedirect = async () => {
    setLoading(true);
    try {
      let res;
      if (paymentType === 'add_balance') {
        res = await paymentsApi.topupBalance(selectedPackage, window.location.origin);
      } else if (paymentType === 'subscription') {
        res = await paymentsApi.subscribe('premium', window.location.origin);
      } else {
        const payload = {
          payment_type: paymentType,
          origin_url: window.location.origin,
          creator_id: creatorId || undefined,
          product_id: productId || undefined,
          film_id: filmId || undefined,
          amount: paymentType === 'donation' ? parseFloat(customDonation) : undefined,
          metadata: {},
        };
        res = await paymentsApi.createCheckout(payload);
      }

      if (res.checkout_url || res.status === 'checkout_required') {
        window.location.href = res.checkout_url;
      } else if (res.status === 'completed' || res.status === 'active') {
        setResult(res);
        toast.success('Payment successful!');
        if (onSuccess) onSuccess(res);
      }
    } catch (error) {
      toast.error(error.message || 'Checkout failed');
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (paymentType) {
      case 'add_balance': return 'Add Balance';
      case 'donation': return 'Send Donation';
      case 'subscription': return 'Subscribe to Premium';
      case 'merchandise': return 'Purchase Item';
      case 'premium_content': return 'Unlock Content';
      default: return 'Payment';
    }
  };

  const canUseBalance = paymentType === 'subscription' && userBalance > 0;

  if (result) {
    return createPortal(
      <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="stripe-checkout-modal">
        <div className="bg-[#0F0B1E] border border-[#8B5CF6]/20 rounded-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="p-8 text-center">
            <CheckCircle className="w-14 h-14 text-[#00FF9D] mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Payment Successful!</h2>
            {result.amount && (
              <p className="text-gray-400 text-sm mb-1">{result.amount.toFixed(2)} USD processed</p>
            )}
            {result.new_balance !== undefined && (
              <p className="text-sm text-[#6EE7A8]">New balance: {result.new_balance.toFixed(2)} USD</p>
            )}
            {result.method && (
              <p className="text-xs text-gray-500 mt-2">
                Paid via {result.method === 'saved_card' ? 'saved card' : result.method === 'balance' ? 'balance' : result.method}
              </p>
            )}
            <button onClick={onClose}
              className="mt-6 px-8 py-2.5 bg-[#8B5CF6] hover:bg-[#7C3AED] rounded-xl text-white font-medium transition-colors"
              data-testid="checkout-done-btn">
              Done
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="stripe-checkout-modal">
      <div className="bg-[#0F0B1E] border border-[#8B5CF6]/20 rounded-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-5 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#8B5CF6]/20 flex items-center justify-center">
              {paymentType === 'subscription' ? <Zap className="w-5 h-5 text-[#8B5CF6]" /> : <CreditCard className="w-5 h-5 text-[#8B5CF6]" />}
            </div>
            <div>
              <h2 className="text-lg font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{getTitle()}</h2>
              <p className="text-xs text-gray-500">Secure payment via Stripe</p>
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
            data-testid="checkout-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {loadingInfo ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-6 h-6 text-[#8B5CF6] animate-spin" />
            </div>
          ) : (
            <>
              {/* Balance Package Selector */}
              {paymentType === 'add_balance' && (
                <div>
                  <p className="text-sm text-gray-400 mb-3">Select amount to add:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {BALANCE_PACKAGES.map(pkg => (
                      <button key={pkg.id} onClick={() => setSelectedPackage(pkg.id)}
                        className={`p-3 rounded-xl border text-center transition-all ${
                          selectedPackage === pkg.id
                            ? 'border-[#8B5CF6] bg-[#8B5CF6]/10 text-white'
                            : 'border-white/10 bg-white/[0.02] text-gray-400 hover:border-white/20'
                        }`} data-testid={`balance-pkg-${pkg.id}`}>
                        <span className="text-xl font-bold">{pkg.label}</span>
                        <span className="text-xs text-gray-500 block">USD</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Donation Amount */}
              {paymentType === 'donation' && (
                <div>
                  <p className="text-sm text-gray-400 mb-3">Donation amount (USD):</p>
                  <div className="flex gap-2 mb-3">
                    {[0.50, 2.00, 5.00].map(a => (
                      <button key={a} onClick={() => setCustomDonation(a)}
                        className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-all ${
                          customDonation === a
                            ? 'border-[#8B5CF6] bg-[#8B5CF6]/10 text-white'
                            : 'border-white/10 text-gray-400 hover:border-white/20'
                        }`}>
                        {a.toFixed(2)}
                      </button>
                    ))}
                  </div>
                  <input type="number" min="0.50" max="500" step="0.50"
                    value={customDonation}
                    onChange={e => setCustomDonation(parseFloat(e.target.value) || 0.50)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-center text-lg font-medium focus:outline-none focus:border-[#8B5CF6]"
                    data-testid="donation-amount-input" />
                </div>
              )}

              {/* Subscription Info */}
              {paymentType === 'subscription' && (
                <div className="text-center py-2">
                  <p className="text-3xl font-bold text-white mb-1">3.99 <span className="text-base text-gray-400">USD/mo</span></p>
                  <p className="text-sm text-gray-400">Premium Plan  — Ad-free experience</p>
                  {canUseBalance && (
                    <div className="mt-3 p-3 rounded-lg bg-[#6EE7A8]/10 border border-[#6EE7A8]/20">
                      <div className="flex items-center justify-center gap-2 text-sm">
                        <Wallet className="w-4 h-4 text-[#6EE7A8]" />
                        <span className="text-[#6EE7A8]">Your balance: {userBalance.toFixed(2)} USD</span>
                      </div>
                      {userBalance >= 3.99 && (
                        <p className="text-xs text-gray-400 mt-1">Will be charged from your balance</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Generic info for other types */}
              {(paymentType === 'merchandise' || paymentType === 'premium_content') && (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-400">Complete your purchase securely via Stripe.</p>
                </div>
              )}

              {/* Saved Card Info */}
              {savedCard && (
                <div className="p-3 rounded-xl bg-[#8B5CF6]/5 border border-[#8B5CF6]/15">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-7 rounded bg-white/10 flex items-center justify-center">
                      <CreditCard className="w-4 h-4 text-[#8B5CF6]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium" data-testid="modal-saved-card">
                        {savedCard.brand} **** {savedCard.last4}
                      </p>
                      <p className="text-[11px] text-gray-500">Expires {savedCard.exp_month}/{savedCard.exp_year}</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer - Payment Actions */}
        {!loadingInfo && (
          <div className="p-5 border-t border-white/5 space-y-2">
            {/* Primary action: pay with saved card or balance */}
            {(savedCard || canUseBalance) && (
              <button onClick={handlePayWithSavedCard} disabled={loading}
                className="w-full py-3 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold flex items-center justify-center gap-2 transition-colors"
                data-testid="checkout-pay-saved-btn">
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
                ) : savedCard ? (
                  <><CreditCard className="w-4 h-4" /> Pay with {savedCard.brand} *{savedCard.last4}</>
                ) : (
                  <><Wallet className="w-4 h-4" /> Pay from Balance</>
                )}
              </button>
            )}

            {/* Secondary action: checkout redirect */}
            <button onClick={handleCheckoutRedirect} disabled={loading}
              className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors ${
                savedCard || canUseBalance
                  ? 'bg-white/5 hover:bg-white/10 text-gray-300 border border-white/10'
                  : 'bg-[#8B5CF6] hover:bg-[#7C3AED] text-white'
              }`}
              data-testid="checkout-pay-btn">
              {loading && !(savedCard || canUseBalance) ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
              ) : savedCard || canUseBalance ? (
                'Pay with different method'
              ) : (
                <><CreditCard className="w-4 h-4" /> Proceed to Payment</>
              )}
            </button>

            <p className="text-[10px] text-gray-600 text-center mt-2">
              Payments handled securely by Stripe. We never store your card details.
            </p>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default StripeCheckoutModal;
